import type {
  Components,
  PaletteColorOptions,
  ThemeOptions,
  TypeBackground,
  TypeText,
} from "@mui/material";
import { alpha } from "@mui/material";
import type { SidebarColorOptions, StatusColorOptions } from ".";
import { neutral } from "./base-theme-options";

// Colors

const background: TypeBackground = {
  default: "#F9FAFC",
  paper: "#FFFFFF",
  active: "rgba(17, 25, 39, 0.04)",
  contrast: "#EFEFEF",
  baseTrack: "#1890FF",
  switchTrack: "rgba(0,0,0,.25)",
  sidebar: neutral[900],
  drawer: "#5048E5",
  selected: alpha("#828DF8", 0.2),
};

const sidebar: SidebarColorOptions = {
  hover: "rgba(255, 255, 255, 0.04)",
  divider: "#2D3748",
};

const divider = "#E6E8F0";

const primary: PaletteColorOptions = {
  main: "#5048E5",
  light: "#828DF8",
  dark: "#3832A0",
  contrastText: "#FFFFFF",
};

const secondary: PaletteColorOptions = {
  main: "#10B981",
  light: "#3FC79A",
  dark: "#0B815A",
  contrastText: "#FFFFFF",
};

const success: PaletteColorOptions = {
  main: "#14B8A6",
  light: "#43C6B7",
  dark: "#0E8074",
  contrastText: "#FFFFFF",
};

const info: PaletteColorOptions = {
  main: "#2196F3",
  light: "#64B6F7",
  dark: "#0B79D0",
  contrastText: "#FFFFFF",
};

const warning: PaletteColorOptions = {
  main: "#FFB020",
  light: "#FFBF4C",
  dark: "#B27B16",
  contrastText: "#FFFFFF",
};

const error: PaletteColorOptions = {
  main: "#D14343",
  light: "#DA6868",
  dark: "#922E2E",
  contrastText: "#FFFFFF",
};

const disabled: PaletteColorOptions = {
  main: neutral[400],
  light: neutral[400],
  dark: neutral[400],
  contrastText: "#FFFFFF",
};

const momentStatus: StatusColorOptions = {
  draft: warning.main,
  archived: disabled.main,
  active: error.main,
  finished: info.main,
  paused: disabled.main,
};

const text: TypeText = {
  primary: "#121828",
  secondary: "#65748B",
  disabled: "rgba(55, 65, 81, 0.48)",
  discreet: neutral[500],
  sidebar: "#DBE0E5",
};

const PageHeader: Components["PageHeader"] = {
  styleOverrides: {
    background:
      "linear-gradient(0deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.9)), #64B6F7",
  },
};

const ListenPageHeader: Components["ListenPageHeader"] = {
  styleOverrides: {
    background:
      "linear-gradient(270deg, rgba(177, 79, 150, 0.1) 0%, rgba(129, 73, 156, 0.1) 50%, rgba(35, 181, 235, 0.1) 99.52%),linear-gradient(180deg, rgba(255, 255, 255, 0) -364.37%, rgba(255, 255, 255, 0.4) 90.67%)",
  },
};

export const lightThemeOptions: ThemeOptions & {
  components: {
    MuiDataGrid: unknown;
  };
} = {
  components: {
    PageHeader,
    ListenPageHeader,
    MomentEditorSidebar: {
      styleOverrides: {
        borderColor: neutral[300],
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: background.paper,
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: neutral[500],
          color: "#FFFFFF",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          "&.MuiChip-filledDefault": {
            backgroundColor: neutral[200],
            "& .MuiChip-deleteIcon": {
              color: neutral[400],
            },
          },
          "&.MuiChip-outlinedDefault": {
            "& .MuiChip-deleteIcon": {
              color: neutral[300],
            },
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          "&::placeholder": {
            opacity: 1,
            color: text.secondary,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: divider,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderColor: divider,
          borderStyle: "solid",
          borderWidth: 1,
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          borderColor: divider,
          borderStyle: "solid",
          borderWidth: 1,
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          color: neutral[500],
        },
        track: {
          backgroundColor: neutral[400],
          opacity: 1,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${divider}`,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: neutral[100],
          ".MuiTableCell-root": {
            color: neutral[700],
          },
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          "& .MuiDataGrid-cellContent:hover": {
            zIndex: 999,
            overflow: "visible",
            paddingLeft: "1em",
            paddingRight: "1em",
            display: "block",
            top: "2em",
            color: info.contrastText,
            background: info.main,
            textAlign: "center",
            borderRadius: "8px",
            boxShadow: "4px 3px 10px #7582EB",
          },
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          "& .MuiToggleButtonGroup-grouped:not(:first-of-type).Mui-selected, & .Mui-selected": {
            border: "1px solid",
            borderColor: `${primary.main}22`,
          },
        },
      },
    },
  },
  palette: {
    action: {
      active: neutral[500],
      focus: "rgba(55, 65, 81, 0.12)",
      hover: "rgba(55, 65, 81, 0.04)",
      selected: "rgba(55, 65, 81, 0.08)",
      disabledBackground: "rgba(55, 65, 81, 0.12)",
      disabled: "rgba(55, 65, 81, 0.26)",
    },
    background,
    divider,
    error,
    info,
    mode: "light",
    neutral,
    primary,
    secondary,
    success,
    text,
    warning,
    disabled,
    sidebar,
    status: momentStatus,
  },
  shadows: [
    "none",
    "0px 1px 1px rgba(100, 116, 139, 0.06), 0px 1px 2px rgba(100, 116, 139, 0.1)",
    "0px 1px 2px rgba(100, 116, 139, 0.12)",
    "0px 1px 4px rgba(100, 116, 139, 0.12)",
    "0px 1px 5px rgba(100, 116, 139, 0.12)",
    "0px 1px 6px rgba(100, 116, 139, 0.12)",
    "0px 2px 6px rgba(100, 116, 139, 0.12)",
    "0px 3px 6px rgba(100, 116, 139, 0.12)",
    "0px 2px 4px rgba(31, 41, 55, 0.06), 0px 4px 6px rgba(100, 116, 139, 0.12)",
    "0px 5px 12px rgba(100, 116, 139, 0.12)",
    "0px 5px 14px rgba(100, 116, 139, 0.12)",
    "0px 5px 15px rgba(100, 116, 139, 0.12)",
    "0px 6px 15px rgba(100, 116, 139, 0.12)",
    "0px 7px 15px rgba(100, 116, 139, 0.12)",
    "0px 8px 15px rgba(100, 116, 139, 0.12)",
    "0px 9px 15px rgba(100, 116, 139, 0.12)",
    "0px 10px 15px rgba(100, 116, 139, 0.12)",
    "0px 12px 22px -8px rgba(100, 116, 139, 0.25)",
    "0px 13px 22px -8px rgba(100, 116, 139, 0.25)",
    "0px 14px 24px -8px rgba(100, 116, 139, 0.25)",
    "0px 10px 10px rgba(31, 41, 55, 0.04), 0px 20px 25px rgba(31, 41, 55, 0.1)",
    "0px 25px 50px rgba(100, 116, 139, 0.25)",
    "0px 25px 50px rgba(100, 116, 139, 0.25)",
    "0px 25px 50px rgba(100, 116, 139, 0.25)",
    "0px 25px 50px rgba(100, 116, 139, 0.25)",
  ],
};
